<template>
  <section
    ref="root"
    class="relative z-20 min-w-[260px] pb-2 transition-all duration-150 max-lg:flex min-[1000px]:max-[1100px]:!max-w-[155px]"
    role="search"
    @click="toggle"
  >
    <form
      ref="searchBox"
      aria-haspopup="listbox"
      class="w-full"
      data-testid="search-form"
      role="search"
      @submit.prevent="goToSearchResolutionOrSearchPage"
    >
      <div
        :aria-label="$t('global.search')"
        :class="{
          'border-gray-500 bg-white': hasFocus,
          'border-gray-400 bg-gray-100': !hasFocus,
        }"
        class="group flex h-11 items-center gap-2 overflow-hidden rounded-full border px-3 transition-all duration-150 max-lg:grow lg:h-10"
        @click="openAndFocus"
      >
        <!-- Search icon (decorative) -->
        <SFButton
          aria-hidden="true"
          class="flex items-center justify-center"
          tabindex="-1"
          variant="raw"
        >
          <IconSearch
            class="size-6 shrink-0 text-gray-500 min-[1000px]:max-[1024px]:size-7"
          />
        </SFButton>

        <!-- Search input -->
        <div class="flex h-full min-w-0 grow items-center gap-2">
          <label :for="id" class="hidden">{{ $t('global.search') }}</label>
          <input
            :id="id"
            ref="input"
            v-model.trim="searchQuery"
            :placeholder="$t('search_input.placeholder')"
            class="min-w-0 grow bg-transparent font-sans text-lg text-gray-600 placeholder:font-sans placeholder:text-gray-600 placeholder-shown:truncate focus-visible:shadow-none focus-visible:outline-none"
            data-testid="header-search-input"
            type="search"
          />
        </div>
      </div>
    </form>
  </section>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, useId, watch } from 'vue'
import {
  onClickOutside,
  onKeyStroke,
  useDebounceFn,
  useEventListener,
} from '@vueuse/core'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import type { SearchEntity } from '@scayle/storefront-nuxt'

import { useRouteHelpers } from '~/composables'
import { useSearchInputKeybindings } from '~/composables/useSearchInputKeybindings'
import { SFButton } from '#storefront-ui/components'

import { useSearch } from '#storefront-search/composables/useSearch'
import { DEBOUNCED_SEARCH_DURATION } from '~/constants'
import { useSlideIn } from '~/modules/ui/runtime/composables'
import { useSearchFlyout } from '~/composables/useSearchFlyout'

const emit = defineEmits<{
  close: []
  clickResult: [result: SearchEntity | 'show_all']
}>()

const { getSearchRoute, localizedNavigateTo, getSearchSuggestionPath } =
  useRouteHelpers()

const { toggle, isOpen } = useSlideIn('SearchFlyout')

watch(isOpen, (value) => {
  if (value) {
    input.value?.blur()
  }
})

const {
  searchQuery,
  getSearchSuggestions,
  resetSearch,
  totalCount,
  resolveSearch,
} = useSearch()

const id = computed(() => `search-input-${useId()}`)

const debouncedSearch = useDebounceFn(async () => {
  await getSearchSuggestions()
}, DEBOUNCED_SEARCH_DURATION)

watch(
  () => searchQuery.value,
  async () => {
    await debouncedSearch()
  },
)

const root = ref<HTMLElement>()
const input = ref<HTMLInputElement>()
const resultContainer = ref()
const searchBox = ref<HTMLElement>()
const resetButton = ref<HTMLButtonElement>()
const hasFocus = ref(false)

const { setRootOffsetTop } = useSearchFlyout()

const reset = async () => {
  resetSearch()
  hasFocus.value = false
  await nextTick()
  searchBox.value?.focus()
}

const closeAndReset = async () => {
  await reset()
  emit('close')
}

const openAndFocus = () => {
  input.value?.focus()
  hasFocus.value = true
}

const goToSearchResolutionOrSearchPage = async () => {
  if (!searchQuery.value) {
    return
  }

  const resolved = await resolveSearch()
  if (resolved?.type) {
    const route = getSearchSuggestionPath(resolved)
    if (route) {
      await localizedNavigateTo(route)
      await closeAndReset()
      return
    }
  }

  const route = getSearchRoute(searchQuery.value)

  await localizedNavigateTo(route)

  await closeAndReset()
}

const ARROW_KEYS = ['ArrowUp', 'ArrowDown']

onMounted(async () => {
  await nextTick()
  setRootOffsetTop(root.value?.offsetTop ?? 0)
})

onKeyStroke(
  ARROW_KEYS,
  // Prevent scrolling the page on arrow keys
  (event: KeyboardEvent) => event.preventDefault(),
  { target: root },
)

onClickOutside(root, () => {
  if (!hasFocus.value) {
    return
  }
  reset()
})

const { activate, deactivate } = useFocusTrap(resultContainer, {
  isKeyBackward: (keyEvent: KeyboardEvent) => keyEvent.code === 'ArrowUp',
  isKeyForward: (keyEvent: KeyboardEvent) => keyEvent.code === 'ArrowDown',
  allowOutsideClick: true,
  returnFocusOnDeactivate: false,
})

useEventListener(resultContainer, 'focusin', () => activate())

useSearchInputKeybindings(
  input,
  resultContainer,
  searchBox,
  resetButton,
  hasFocus,
  activate,
  deactivate,
  openAndFocus,
  reset,
  closeAndReset,
  searchQuery,
  totalCount,
)
</script>
